import { API_BASE_URL, getAuthHeaders } from "@/lib/api-client";
import type {
  Exam,
  ExamQuestion,
  ExamSession,
  GradingResponse,
  GradingQueueItem,
  GradedHistoryItem,
} from "@/lib/types";
import type { PaginatedResponse } from "@/lib/types";

export interface ViolationResult {
  logged: boolean;
  violation_count: number;
  max_violations: number;
  terminated: boolean;
  /** Id of the stored violation, used to attach the webcam frame captured at that moment. */
  violation_id?: string | null;
  /** True when the server recognised this as a retry of a violation it had already recorded. */
  duplicate?: boolean;
}

/**
 * Mirrors the backend `ExamLockReason` enum (ExamStartPolicy.cs). The server evaluates these in a
 * fixed precedence order and reports the FIRST one that blocked the start, so exactly one applies.
 */
export type ExamLockReasonCode =
  | "WindowNotOpen"
  | "WindowClosed"
  | "ProgressionLocked"
  | "AwaitingGrading"
  | "CheckpointAlreadyPassed"
  | "AttemptsExhausted"
  | "NotEnrolled";

/** Sub-category of a "ProgressionLocked" verdict — mirrors ExamCheckpointGateDto.LockReason. */
export type ExamGateLockReason =
  | "remedial"
  | "previous_module_incomplete"
  | "video_checkpoint_not_reached"
  | "checkpoint"
  | "modules_incomplete"
  | "course_incomplete"
  | "track_incomplete";

export interface AvailableExam {
  exam_id: string;
  course_id: string | null;
  checkpoint_id: string | null;
  /** Backend ExamType string: "PracticeQuiz" | "CheckpointExam" | "CourseFinal" | "Assignment" | "Survey" | "TrackFinal". */
  exam_type: string;
  title: string;
  description: string | null;
  time_limit_minutes: number;
  is_biometric_required: boolean;
  passing_score: number;
  max_violations: number;
  exam_status: "locked" | "in_progress" | "completed" | "failed" | "ready" | "pending_grading";
  session_id: string | null;
  has_active_session: boolean;
  can_resume: boolean;
  can_retry: boolean;
  /**
   * Convenience negation of `is_locked` — "can this be started right now". Do NOT use it to decide
   * WHY an exam is locked: it is true for a spent attempt, a closed window and an unreached
   * prerequisite alike. Branch on `lock_reason_code` for that.
   */
  is_ready: boolean;
  is_completed: boolean;
  is_locked: boolean;
  completed_modules: number;
  total_modules: number;
  attempts_taken: number;
  /** Attempts left before an admin must reopen the exam. null = unlimited (PracticeQuiz). */
  remaining_attempts: number | null;
  score: number | null;
  already_passed: boolean;
  /** Human-readable lock explanation, for display. */
  lock_reason: string | null;
  /**
   * Machine-readable lock category from the server. This is the ONLY correct discriminant for lock
   * state — `is_locked` is a single aggregate flag covering every reason at once.
   */
  lock_reason_code: ExamLockReasonCode | null;
  /** When `lock_reason_code` is "ProgressionLocked", which progression rule blocked it. */
  gate_lock_reason: ExamGateLockReason | null;
  next_required_module: string | null;
  certificate_eligible: boolean;
  certificate_id: string | null;
  certificate_code: string | null;
  course_title: string | null;
  checkpoint_title: string | null;
  failure_action: string | null;
  is_checkpoint: boolean;
  /** True when this checkpoint exam is locked because a prior attempt FAILED and its covered modules were reset — the student must rewatch before retrying. */
  requires_remedial_rewatch: boolean;
  is_final: boolean;
  completion_percentage: number;
  /** Server-computed categorical status: Upcoming | Available | InProgress | Completed | PendingGrading | Missed | Expired | Locked. */
  status: string;
  start_date: string | null;
  end_date: string | null;
  // ── Pending-grading breakdown (populated only when the last session awaits manual grading) ──
  last_auto_graded_points: number | null;
  last_auto_graded_total: number | null;
  last_total_points: number | null;
  last_pending_essay_count: number;
  last_total_essay_count: number;
  /** When non-null, an admin reopened this session and left this reason for the student. */
  reopen_reason: string | null;
}

export async function fetchActiveExams(isAdmin?: boolean): Promise<Exam[]> {
  if (!API_BASE_URL) {
    return [];
  }
  // There is no `/exams/active` route. The backend `GET /Exams` derives admin status
  // from the JWT (never a query param): admins get every non-deleted exam (drafts
  // included when `includeInactive`), students get only their assigned active+visible
  // exams. We reuse `fetchExams`, which already maps camelCase → the snake_case fields
  // the `Exam` interface exposes, then keep only active exams for non-admin callers.
  const exams = await fetchExams(!!isAdmin);
  return (isAdmin ? exams : exams.filter((ex: any) => ex.is_active !== false)) as Exam[];
}

export async function fetchPastExamSession(
  userId: string,
  examId: string,
): Promise<
  | (Pick<ExamSession, "passed" | "score" | "completed_at" | "status"> & { attempt_count: number })
  | null
> {
  if (!API_BASE_URL) {
    return null;
  }
  const res = await fetch(`${API_BASE_URL}/exams/${examId}/sessions/past?userId=${userId}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error("Failed to fetch past exam session");
  return await res.json();
}

export async function fetchAllPastExamSessions(
  userId: string,
  examIds: string[],
): Promise<
  Record<
    string,
    {
      session_id: string | null;
      passed: boolean | null;
      score: number | null;
      completed_at: string | null;
      status: string;
      attempt_count: number;
      /**
       * When the student's most recent PASS on this exam was voided for progression, because staff
       * authorized a new attempt over it. Null for the ordinary case and for a student who never
       * passed.
       *
       * This is the ONLY thing that distinguishes "never passed this" from "passed this and it was
       * reset" on the student's own screens — `passed` above is already false in both cases, by
       * design, because it answers the gating question. Without it a student sees a locked
       * checkpoint above their own 100% and has nothing to reconcile the two with.
       */
      superseded_pass_at: string | null;
      /** The reason staff gave, verbatim. Null when none was given. */
      superseded_pass_reason: string | null;
    }
  >
> {
  if (examIds.length === 0) return {};
  if (!API_BASE_URL) {
    return {};
  }
  const res = await fetch(
    `${API_BASE_URL}/exams/sessions/past/batch?userId=${userId}&examIds=${examIds.join(",")}`,
    { headers: getAuthHeaders() },
  );
  if (!res.ok) throw new Error("Failed to fetch past exam sessions");
  const raw = await res.json();
  const result: Record<string, any> = {};
  for (const key of Object.keys(raw)) {
    const val = raw[key];
    result[key] = {
      session_id: val.sessionId ?? val.session_id ?? null,
      passed: val.passed,
      score: val.score,
      completed_at: val.completedAt ?? val.completed_at ?? null,
      status: val.status,
      attempt_count: val.attemptCount ?? val.attempt_count ?? 0,
      superseded_pass_at: val.supersededPassAt ?? val.superseded_pass_at ?? null,
      superseded_pass_reason: val.supersededPassReason ?? val.superseded_pass_reason ?? null,
    };
  }
  return result;
}

export async function fetchSessionQuestions(sessionId: string): Promise<ExamQuestion[]> {
  if (!API_BASE_URL) {
    return [];
  }
  const res = await fetch(`${API_BASE_URL}/exams/sessions/${sessionId}/questions`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error("Failed to fetch session questions");
  return await res.json();
}

export interface SessionResponseState {
  question_id: string;
  selected_answer: string;
  grading_status: string;
}

export interface SessionState {
  session_id: string;
  status: string;
  expires_at: string;
  seconds_remaining: number;
  violation_count: number;
  max_violations: number;
  responses: SessionResponseState[];
  /** Caller IP as the server resolved it, for the anti-leak watermark. */
  client_ip_address: string | null;
  /** Server-decided webcam cadence, scaled to the exam's duration. */
  snapshot_interval_seconds: number;
  /** Server-decided heartbeat cadence. */
  heartbeat_interval_seconds: number;
  /**
   * Seconds a student gets to restore a webcam that dropped mid-attempt before the client ends the
   * session. Server-decided for the same reason the two cadences are.
   */
  camera_grace_seconds: number;
  /**
   * True when this session was reopened by a Team Leader and the student has not yet re-proved their
   * identity. The client MUST route such a resume through the biometric phase rather than straight
   * into the questions.
   *
   * The server sets it, but cannot enforce it alone: its own gate lives in the create-session
   * endpoint, and a resume never calls that endpoint — the client just fetches the questions for a
   * session it already has. Four completed production sessions carried `IsBiometricRequired` with
   * `BiometricConfirmed` still false because of exactly that, so this flag is the enforcement point.
   */
  requires_biometric_re_verification: boolean;
}

// Resumable state of a session: server-authoritative time left, violation count, and
// the answers already saved — used to rehydrate the exam UI on resume/refresh.
export async function fetchSessionState(sessionId: string): Promise<SessionState | null> {
  if (!API_BASE_URL) {
    return null;
  }
  const res = await fetch(`${API_BASE_URL}/exams/sessions/${sessionId}/state`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return null;
  const s = await res.json();
  return {
    session_id: s.sessionId ?? s.session_id,
    status: s.status,
    expires_at: s.expiresAt ?? s.expires_at,
    seconds_remaining: s.secondsRemaining ?? s.seconds_remaining ?? 0,
    violation_count: s.violationCount ?? s.violation_count ?? 0,
    max_violations: s.maxViolations ?? s.max_violations ?? 0,
    client_ip_address: s.clientIpAddress ?? s.client_ip_address ?? null,
    // Fall back to the server's own floor/default rather than 0: a 0 would read as "capture
    // continuously" or "never beat" depending on the consumer, and both are wrong.
    snapshot_interval_seconds: s.snapshotIntervalSeconds ?? s.snapshot_interval_seconds ?? 30,
    heartbeat_interval_seconds: s.heartbeatIntervalSeconds ?? s.heartbeat_interval_seconds ?? 20,
    camera_grace_seconds: s.cameraGraceSeconds ?? s.camera_grace_seconds ?? 60,
    // Defaults to false, so a server too old to send it cannot lock every resume out of its own
    // exam. Missing the flag re-opens a known hole; inventing it breaks every running attempt.
    requires_biometric_re_verification:
      s.requiresBiometricReVerification ?? s.requires_biometric_re_verification ?? false,
    responses: (s.responses ?? []).map((r: any) => ({
      question_id: r.questionId ?? r.question_id,
      selected_answer: r.selectedAnswer ?? r.selected_answer ?? "",
      grading_status: r.gradingStatus ?? r.grading_status ?? "AutoGraded",
    })),
  };
}

export async function createExamSession(
  userId: string,
  examId: string,
  biometricConfirmed: boolean,
  biometricImageUrl?: string | null,
): Promise<string> {
  if (!API_BASE_URL) {
    throw new Error("Backend not implemented");
  }
  const res = await fetch(`${API_BASE_URL}/exams/sessions`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ examId, biometricConfirmed, biometricImageUrl, userId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || res.statusText);
  }
  const { id } = await res.json();
  return id;
}

export async function saveExamResponse(params: {
  sessionId: string;
  questionId: string;
  selectedAnswer?: string;
  selectedAnswers?: string[];
  timeTakenSeconds: number;
}): Promise<void> {
  if (!API_BASE_URL) {
    throw new Error("Backend not implemented");
  }
  const res = await fetch(`${API_BASE_URL}/exams/sessions/${params.sessionId}/responses`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      questionId: params.questionId,
      selectedAnswer: params.selectedAnswer,
      selectedAnswers: params.selectedAnswers,
      timeTakenSeconds: params.timeTakenSeconds,
    }),
  });
  if (!res.ok) throw new Error("Failed to save response");
}

export async function saveEssayResponse(params: {
  sessionId: string;
  questionId: string;
  textAnswer: string;
  timeTakenSeconds?: number;
}): Promise<void> {
  if (!API_BASE_URL) {
    throw new Error("Backend not implemented");
  }
  const res = await fetch(`${API_BASE_URL}/exams/sessions/${params.sessionId}/responses/essay`, {
    method: "POST",
    headers: getAuthHeaders(),
    // Backend expects { questionId, essayText, timeTakenSeconds }
    body: JSON.stringify({
      questionId: params.questionId,
      essayText: params.textAnswer,
      timeTakenSeconds: params.timeTakenSeconds,
    }),
  });
  if (!res.ok) throw new Error("Failed to save essay response");
}

export interface CompleteExamResult {
  score: number;
  passed: boolean;
  correct: number;
  total: number;
  pending_manual_grading: boolean;
  auto_graded_points: number;
  auto_graded_total: number;
  total_points: number;
  pending_essay_count: number;
}

export async function completeExamSession(sessionId: string): Promise<CompleteExamResult> {
  if (!API_BASE_URL) {
    throw new Error("Backend not implemented");
  }
  const res = await fetch(`${API_BASE_URL}/exams/sessions/${sessionId}/complete`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error("Failed to complete exam session");
  const r = await res.json();
  return {
    score: r.score ?? 0,
    passed: r.passed ?? false,
    correct: r.correct ?? 0,
    total: r.total ?? 0,
    pending_manual_grading: r.pendingManualGrading ?? r.pending_manual_grading ?? false,
    auto_graded_points: r.autoGradedPoints ?? r.auto_graded_points ?? 0,
    auto_graded_total: r.autoGradedTotal ?? r.auto_graded_total ?? 0,
    total_points: r.totalPoints ?? r.total_points ?? 0,
    pending_essay_count: r.pendingEssayCount ?? r.pending_essay_count ?? 0,
  };
}

// ── Post-exam review (gated by exam ReviewPolicy) ─────────────────────────
export interface SessionReviewQuestion {
  question_id: string;
  question_text: string;
  question_type: string;
  options: string[];
  your_answer: string;
  correct_answers: string[];
  is_correct: boolean | null;
  points: number;
  points_earned: number;
  grading_status: string;
  grader_note: string | null;
}

export interface SessionReview {
  session_id: string;
  available: boolean;
  reason: string | null;
  score: number | null;
  passed: boolean | null;
  status: string;
  terminated: boolean;
  questions: SessionReviewQuestion[];
}

export async function fetchSessionReview(sessionId: string): Promise<SessionReview | null> {
  if (!API_BASE_URL) return null;
  const res = await fetch(`${API_BASE_URL}/exams/sessions/${sessionId}/review`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return null;
  const r = await res.json();
  return {
    session_id: r.sessionId ?? r.session_id,
    available: r.available ?? false,
    reason: r.reason ?? null,
    score: r.score ?? null,
    passed: r.passed ?? null,
    status: r.status ?? r.Status ?? "",
    terminated: r.terminated ?? r.Terminated ?? false,
    questions: (r.questions ?? []).map((q: any) => ({
      question_id: q.questionId ?? q.question_id,
      question_text: q.questionText ?? q.question_text ?? "",
      question_type: q.questionType ?? q.question_type ?? "MultipleChoice",
      options: q.options ?? [],
      your_answer: q.yourAnswer ?? q.your_answer ?? "",
      correct_answers: q.correctAnswers ?? q.correct_answers ?? [],
      is_correct: q.isCorrect ?? q.is_correct ?? null,
      points: q.points ?? 1,
      points_earned: q.pointsEarned ?? q.points_earned ?? 0,
      grading_status: q.gradingStatus ?? q.grading_status ?? "AutoGraded",
      grader_note: q.graderNote ?? q.grader_note ?? null,
    })),
  };
}

// ── Question tags ─────────────────────────────────────────────────────────
export async function fetchQuestionTags(): Promise<
  { id: string; name: string; question_count: number }[]
> {
  if (!API_BASE_URL) return [];
  const res = await fetch(`${API_BASE_URL}/QuestionBanks/tags`, { headers: getAuthHeaders() });
  if (!res.ok) return [];
  const raw = await res.json();
  return (Array.isArray(raw) ? raw : []).map((t: any) => ({
    id: t.id,
    name: t.name,
    question_count: t.questionCount ?? t.question_count ?? 0,
  }));
}

export async function createQuestionTag(name: string): Promise<any> {
  const res = await fetch(`${API_BASE_URL}/QuestionBanks/tags`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ name }),
  });
  return jsonOrThrow(res);
}

// Dependency warning before editing/deleting a bank question.
export async function fetchQuestionUsageWarning(questionId: string): Promise<any> {
  if (!API_BASE_URL) return null;
  const res = await fetch(`${API_BASE_URL}/QuestionBanks/questions/${questionId}/usage`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return null;
  const r = await res.json();
  return {
    question_id: r.questionId ?? r.question_id,
    in_use: r.inUse ?? r.in_use ?? false,
    active_exam_count: r.activeExamCount ?? r.active_exam_count ?? 0,
    in_progress_session_count: r.inProgressSessionCount ?? r.in_progress_session_count ?? 0,
    historical_attempt_count: r.historicalAttemptCount ?? r.historical_attempt_count ?? 0,
    message: r.message ?? null,
  };
}

// Persisted per-question usage/difficulty analytics (admin).
export async function fetchQuestionUsageStats(bankId?: string): Promise<any[]> {
  if (!API_BASE_URL) return [];
  const qs = bankId ? `?bankId=${bankId}` : "";
  const res = await fetch(`${API_BASE_URL}/admin/question-usage-stats${qs}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return [];
  return await res.json();
}

export async function terminateExamSession(sessionId: string): Promise<void> {
  if (!API_BASE_URL) {
    throw new Error("Backend not implemented");
  }
  const res = await fetch(`${API_BASE_URL}/exams/sessions/${sessionId}/terminate`, {
    method: "POST",
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error("Failed to terminate exam session");
}

// NOTE: there is deliberately no `recordViolation` here any more. Violations are not sent by a plain
// call from a handler — they go through the durable queue in `lib/proctoring/violation-queue.ts`, which
// persists each one before any network work and retries with backoff until the server acknowledges it.
// A convenience wrapper alongside that queue would be a foot-gun: it would look like the obvious way to
// log a violation while silently reintroducing the fire-and-forget path the queue exists to remove.

/** Server's answer to a heartbeat — its view of the session, so a stale client can correct itself. */
export interface HeartbeatResult {
  accepted: boolean;
  status: string;
  seconds_remaining: number;
  gap_seconds: number;
  gap_flagged: boolean;
}

/**
 * Reports that the client is still present during an active attempt.
 *
 * Returns null on any failure instead of throwing. A missed beat is not an error the student should
 * see — the server is already measuring the silence, and that measurement is the point: a client that
 * stops beating is exactly what the backend's gap detection is looking for, so a failed heartbeat
 * needs no client-side handling beyond not crashing the exam.
 */
export async function sendExamHeartbeat(sessionId: string): Promise<HeartbeatResult | null> {
  if (!API_BASE_URL) return null;
  try {
    const res = await fetch(`${API_BASE_URL}/exams/sessions/${sessionId}/heartbeat`, {
      method: "POST",
      headers: getAuthHeaders(),
    });
    if (!res.ok) return null;
    const r = await res.json();
    return {
      accepted: r.accepted ?? r.Accepted ?? false,
      status: r.status ?? r.Status ?? "",
      seconds_remaining: r.secondsRemaining ?? r.seconds_remaining ?? 0,
      gap_seconds: r.gapSeconds ?? r.gap_seconds ?? 0,
      gap_flagged: r.gapFlagged ?? r.gap_flagged ?? false,
    };
  } catch {
    return null;
  }
}

/**
 * Reports a client-side diagnostic event (e.g. camera unavailable) to the backend audit trail.
 *
 * Unlike violations, this never increments ViolationCount and never triggers termination. It
 * exists purely to explain missing proctoring data to a reviewer — the admin sees "CameraUnavailable"
 * in the audit log instead of an unexplained gap in the snapshot timeline.
 *
 * Fire-and-forget: a failed diagnostic POST should never interrupt the exam.
 */
export async function sendDiagnosticEvent(
  sessionId: string,
  eventType: string,
  detail?: string,
): Promise<void> {
  if (!API_BASE_URL) return;
  try {
    await fetch(`${API_BASE_URL}/exams/sessions/${sessionId}/diagnostics`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ eventType, detail }),
    });
  } catch {
    // Best-effort — the missing frames themselves are already evidence of the issue.
  }
}

// ── Proctoring review (reviewer-facing) ───────────────────────────────────

export interface ProctoringSnapshot {
  id: string;
  captured_at: string;
  /** "Scheduled" | "Violation" | "SessionStart" */
  trigger: string;
  violation_id: string | null;
  violation_type: string | null;
  /** Short-lived presigned URL, minted per request — do not cache or persist. */
  image_url: string;
  /** Which sitting (1-based) this frame belongs to — see {@link ProctoringAttempt}. */
  attempt_index: number;
}

/**
 * One sitting of a session.
 *
 * Historical. The retired reopen action did not create a new session row — it flipped the existing
 * one back to InProgress — so without this split those sittings' frames and violations render as one
 * continuous timeline and a reviewer cannot tell which attempt a violation belongs to. The server
 * reconstructs the boundaries from the append-only audit log. Sessions written by the current
 * moderation actions are always a single sitting.
 */
export interface ProctoringAttempt {
  /** 1-based sitting number. Sitting 1 is the original attempt. */
  attempt_index: number;
  started_at: string;
  /** Null while the sitting is still open (session in progress). */
  ended_at: string | null;
  /** False for the original sitting, true for each one opened by a legacy reopen. */
  is_reopened: boolean;
  reopen_reason: string | null;
  snapshot_count: number;
  violation_count: number;
}

export interface ProctoringTimeline {
  session_id: string;
  /** Exam name — shown as the primary context label throughout the reviewer UI. */
  exam_title: string;
  /**
   * Backend ExamType string: "PracticeQuiz" | "CheckpointExam" | "CourseFinal" |
   * "Assignment" | "Survey" | "TrackFinal". Rendered as a badge next to the title.
   */
  exam_type: string;
  violation_count: number;
  max_violations: number;
  max_heartbeat_gap_seconds: number;
  heartbeat_gap_flagged: boolean;
  last_heartbeat_at: string | null;
  /** Frames this attempt's length should have produced; a much lower actual count is itself a signal. */
  expected_snapshot_count: number;
  /**
   * Server's own verdict on the frame coverage — set when the attempt produced materially fewer frames
   * than its duration should have, including none at all. Advisory: it marks the session for review and
   * never affects the score.
   */
  snapshot_coverage_low: boolean;
  snapshots: ProctoringSnapshot[];
  /** Always at least one entry, so callers can group without special-casing the never-reopened case. */
  attempts: ProctoringAttempt[];
}

/** A session's webcam timeline plus heartbeat summary. Reviewer-only (SystemAdmin or the course's instructor). */
export async function fetchProctoringTimeline(sessionId: string): Promise<ProctoringTimeline | null> {
  if (!API_BASE_URL) return null;
  const res = await fetch(`${API_BASE_URL}/exams/sessions/${sessionId}/proctoring-timeline`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return null;
  const r = await res.json();
  return {
    session_id: r.sessionId ?? r.session_id,
    exam_title: r.examTitle ?? r.exam_title ?? "",
    exam_type: r.examType ?? r.exam_type ?? "",
    violation_count: r.violationCount ?? r.violation_count ?? 0,
    max_violations: r.maxViolations ?? r.max_violations ?? 0,
    max_heartbeat_gap_seconds: r.maxHeartbeatGapSeconds ?? r.max_heartbeat_gap_seconds ?? 0,
    heartbeat_gap_flagged: r.heartbeatGapFlagged ?? r.heartbeat_gap_flagged ?? false,
    last_heartbeat_at: r.lastHeartbeatAt ?? r.last_heartbeat_at ?? null,
    expected_snapshot_count: r.expectedSnapshotCount ?? r.expected_snapshot_count ?? 0,
    snapshot_coverage_low: r.snapshotCoverageLow ?? r.snapshot_coverage_low ?? false,
    snapshots: (r.snapshots ?? []).map((s: any) => ({
      id: s.id ?? s.Id,
      captured_at: s.capturedAt ?? s.captured_at,
      trigger: s.trigger ?? s.Trigger ?? "Scheduled",
      violation_id: s.violationId ?? s.violation_id ?? null,
      violation_type: s.violationType ?? s.violation_type ?? null,
      image_url: s.imageUrl ?? s.image_url ?? "",
      // Default to sitting 1 so a response from a server that predates attempt grouping still
      // renders — it collapses to the single-sitting case rather than an empty timeline.
      attempt_index: s.attemptIndex ?? s.attempt_index ?? 1,
    })),
    attempts: (r.attempts ?? r.Attempts ?? []).map((a: any) => ({
      attempt_index: a.attemptIndex ?? a.attempt_index ?? 1,
      started_at: a.startedAt ?? a.started_at,
      ended_at: a.endedAt ?? a.ended_at ?? null,
      is_reopened: a.isReopened ?? a.is_reopened ?? false,
      reopen_reason: a.reopenReason ?? a.reopen_reason ?? null,
      snapshot_count: a.snapshotCount ?? a.snapshot_count ?? 0,
      violation_count: a.violationCount ?? a.violation_count ?? 0,
    })),
  };
}

// Derive the legacy 5-value UI status from the backend fields. The previous inline expression
// could only ever yield "completed" | "locked" | "ready" — it never produced "in_progress" or
// "failed" despite the type declaring them. Prefer the server's categorical `status` when present.
function deriveExamStatus(item: any): AvailableExam["exam_status"] {
  const backendStatus = String(item.status ?? item.Status ?? "").toLowerCase();
  const lastStatus = String(item.lastStatus ?? item.LastStatus ?? "").toLowerCase();
  const hasActive = !!(item.resumeSessionId ?? item.ResumeSessionId);
  const locked = item.isLocked ?? item.is_locked ?? false;
  const lastPassed = item.lastPassed ?? item.LastPassed;

  if (hasActive || backendStatus === "inprogress" || lastStatus === "inprogress")
    return "in_progress";
  // Pending manual grading — surfaced distinctly and checked before completed (a pending session's
  // lastStatus is "pendinggrading", never "completed").
  if (backendStatus === "pendinggrading" || lastStatus === "pendinggrading")
    return "pending_grading";
  if (lastStatus === "completed" || backendStatus === "completed") {
    return lastPassed === false ? "failed" : "completed";
  }
  if (backendStatus === "expired" || backendStatus === "missed") return "failed";
  if (locked || backendStatus === "locked" || backendStatus === "upcoming") return "locked";
  return "ready";
}

function mapAvailableExam(item: any): AvailableExam {
  const checkpointId = item.checkpointId ?? item.checkpoint_id ?? item.CheckpointId ?? null;
  return {
    exam_id: item.id ?? item.exam_id ?? item.Id,
    course_id: item.courseId ?? item.course_id ?? item.CourseId ?? null,
    checkpoint_id: checkpointId,
    exam_type: item.examType ?? item.exam_type ?? item.ExamType ?? "PracticeQuiz",
    title: item.title ?? item.Title ?? "",
    description: item.description ?? item.Description ?? null,
    time_limit_minutes:
      item.timeLimitMinutes ?? item.time_limit_minutes ?? item.TimeLimitMinutes ?? 0,
    is_biometric_required:
      item.isBiometricRequired ?? item.is_biometric_required ?? item.IsBiometricRequired ?? false,
    passing_score: item.passingScore ?? item.passing_score ?? item.PassingScore ?? 0,
    max_violations: item.maxViolations ?? item.max_violations ?? item.MaxViolations ?? 3,
    exam_status: deriveExamStatus(item),
    session_id: item.resumeSessionId ?? item.session_id ?? item.ResumeSessionId ?? null,
    has_active_session: !!(item.resumeSessionId ?? item.ResumeSessionId),
    can_resume: !!(item.resumeSessionId ?? item.ResumeSessionId),
    // null attemptsRemaining means unlimited (PracticeQuiz); otherwise a retry needs a
    // remaining attempt, which for a gating exam only exists via an admin-granted retake.
    can_retry:
      (item.attemptsRemaining ?? item.remaining_attempts) == null
        ? true
        : (item.attemptsRemaining ?? item.remaining_attempts) > 0,
    is_ready: !(item.isLocked ?? item.is_locked ?? false),
    // Treat a pending-grading attempt as "completed" for card layout purposes (so the attempts /
    // last-attempt blocks render); the distinct pending badge is driven off exam_status.
    is_completed: ["completed", "pendinggrading"].includes(
      String(item.lastStatus ?? item.LastStatus ?? "").toLowerCase(),
    ),
    is_locked: item.isLocked ?? item.is_locked ?? false,
    completed_modules: item.completedModules ?? item.completed_modules ?? 0,
    total_modules: item.totalModules ?? item.total_modules ?? 0,
    attempts_taken: item.attemptsUsed ?? item.attempts_taken ?? 0,
    remaining_attempts: item.attemptsRemaining ?? item.remaining_attempts ?? null,
    score: item.lastScore ?? item.score ?? null,
    already_passed: item.lastPassed ?? item.already_passed ?? false,
    lock_reason: item.lockReason ?? item.lock_reason ?? null,
    lock_reason_code: item.lockReasonCode ?? item.lock_reason_code ?? item.LockReasonCode ?? null,
    gate_lock_reason: item.gateLockReason ?? item.gate_lock_reason ?? item.GateLockReason ?? null,
    next_required_module: item.nextRequiredModule ?? item.next_required_module ?? null,
    certificate_eligible: item.certificateEligible ?? item.certificate_eligible ?? false,
    certificate_id: item.certificateId ?? item.certificate_id ?? null,
    certificate_code: item.certificateCode ?? item.certificate_code ?? null,
    course_title: item.courseTitle ?? item.course_title ?? null,
    checkpoint_title: item.checkpointTitle ?? item.checkpoint_title ?? null,
    failure_action: item.failureAction ?? item.failure_action ?? null,
    // A checkpoint exam is identified authoritatively by the server (it anchors a published
    // checkpoint in the student's training path); fall back to the checkpoint id / legacy type.
    is_checkpoint:
      item.isCheckpoint ??
      item.is_checkpoint ??
      item.IsCheckpoint ??
      (checkpointId != null || item.examType === "checkpoint" || item.examType === "Checkpoint"),
    requires_remedial_rewatch:
      item.requiresRemedialRewatch ??
      item.requires_remedial_rewatch ??
      item.RequiresRemedialRewatch ??
      false,
    is_final: item.examType === "course_final" || item.examType === "CourseFinal",
    completion_percentage: item.completionPercentage ?? item.completion_percentage ?? 0,
    status: item.status ?? item.Status ?? "Available",
    start_date: item.startDate ?? item.start_date ?? item.StartDate ?? null,
    end_date: item.endDate ?? item.end_date ?? item.EndDate ?? null,
    last_auto_graded_points: item.lastAutoGradedPoints ?? item.last_auto_graded_points ?? null,
    last_auto_graded_total: item.lastAutoGradedTotal ?? item.last_auto_graded_total ?? null,
    last_total_points: item.lastTotalPoints ?? item.last_total_points ?? null,
    last_pending_essay_count: item.lastPendingEssayCount ?? item.last_pending_essay_count ?? 0,
    last_total_essay_count: item.lastTotalEssayCount ?? item.last_total_essay_count ?? 0,
    reopen_reason: item.reopenReason ?? item.reopen_reason ?? item.ReopenReason ?? null,
  };
}

export async function fetchAvailableExams(studentId?: string): Promise<AvailableExam[]> {
  if (!API_BASE_URL) {
    return [];
  }
  const res = await fetch(
    `${API_BASE_URL}/exams/available${studentId ? `?studentId=${studentId}` : ""}`,
    { headers: getAuthHeaders() },
  );
  if (!res.ok) throw new Error("Failed to fetch available exams");
  const data = await res.json();
  const list = Array.isArray(data) ? data : data?.items || data?.$values || [];
  return list.map(mapAvailableExam);
}

/* ------------------------------- Admin Operations ------------------------------- */

// PascalCase enum name ("InProgress", "Completed", "PendingGrading") → the
// snake_case status the exam-center Monitor tab filters on ("in_progress", ...).
function toSnakeStatus(status: string | null | undefined): string {
  return (status ?? "")
    .toString()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase();
}

export interface ExamSessionFilterParams {
  search?: string;
  /**
   * Session outcome. Omitted (or "all") means every FINISHED status — the server excludes
   * in-progress attempts by default. Pass "in_progress" to get the live ones, which is what the
   * Monitor tab's Live Sessions list does.
   */
  status?: string;
  hasFlags?: boolean | null;
  /**
   * Webcam/liveness problems: low snapshot coverage, a heartbeat gap, or no frames at all.
   *
   * Separate from `hasFlags`, which asks "did the student do something?". This asks "can we see what
   * the student did?" — and a session with zero violations and zero frames is clean by the first and
   * the entire point of the second.
   */
  cameraIssues?: boolean | null;
  groupId?: string;
  courseId?: string;
  fromDate?: string;
  toDate?: string;
  pageNumber?: number;
  pageSize?: number;
}

// Adapt one camelCase AdminExamSessionDto to the shape the Monitor tab reads
// (nested student, snake_case fields, mapped status).
function mapAdminExamSession(s: any) {
  return {
    id: s.id,
    status: toSnakeStatus(s.status),
    passed: s.passed,
    score: s.score ?? null,
    started_at: s.startedAt ?? null,
    completed_at: s.completedAt ?? null,
    violation_count: s.violationCount ?? 0,
    max_violations: s.maxViolations ?? s.max_violations ?? null,
    exam_title: s.examTitle ?? null,
    exam_id: s.examId ?? s.exam_id ?? null,
    // Whether the two content-resetting actions apply to this exam at all: both define "the content
    // after this exam" from the checkpoint gate, and the server rejects them on course/track finals.
    // Sent by the server rather than derived from the exam type, because anchoring is structural —
    // there is no in-video member on the type enum, so the browser cannot tell an in-video checkpoint
    // from an ordinary exam. Left undefined by an older API build, which is read as "offer it and let
    // the server decide".
    is_checkpoint_anchored: s.isCheckpointAnchored ?? s.is_checkpoint_anchored,
    // Which reattempt operations the SERVER will accept for this session. Read rather than derived:
    // the rewatch operation is forbidden on every kind of final, and the categories are resolved from
    // structure (Course.FinalExamId / Track.FinalExamId) as well as the type column — neither of
    // which the browser can see. Absent on an older API build, which the guards read as "offer it and
    // let the server refuse" (the file's existing convention).
    exam_final_category: s.examFinalCategory ?? s.exam_final_category,
    can_reattempt_with_rewatch: s.canReattemptWithRewatch ?? s.can_reattempt_with_rewatch,
    can_reattempt_preserving_progress:
      s.canReattemptPreservingProgress ?? s.can_reattempt_preserving_progress,
    can_reset_content_after_exam:
      s.canResetContentAfterExam ?? s.can_reset_content_after_exam,
    rewatch_blocked_reason: s.rewatchBlockedReason ?? s.rewatch_blocked_reason ?? null,
    // Only a flag here — the selfie lives in a private bucket, so a usable link is a presigned
    // URL that expires in 15 minutes. The detail view fetches one on demand via
    // fetchSessionEntryPhotoUrl so it is never already stale when opened.
    has_entry_photo: s.hasEntryPhoto ?? false,
    // Webcam proctoring state. Carried on the LIST, not just the per-session timeline, because the
    // question a proctor actually needs answered is "which of today's sessions had no camera?" — and
    // until these were projected there was no way to ask it without opening every session by hand.
    snapshot_count: s.snapshotCount ?? s.snapshot_count ?? 0,
    // Tells "sat without a camera" apart from "never sat at all" — both show zero frames, and only
    // the first is a proctoring finding. Without it every abandoned session in the backlog was
    // badged "attempt unverified".
    answer_count: s.answerCount ?? s.answer_count ?? 0,
    snapshot_coverage_low: s.snapshotCoverageLow ?? s.snapshot_coverage_low ?? false,
    heartbeat_gap_flagged: s.heartbeatGapFlagged ?? s.heartbeat_gap_flagged ?? false,
    last_heartbeat_at: s.lastHeartbeatAt ?? s.last_heartbeat_at ?? null,
    student: { full_name: s.studentName ?? null, email: s.studentEmail ?? null },
    student_id: s.studentId,
    proctoring_logs: (s.proctoringLogs ?? []).map((log: any) => ({
      id: log.id,
      event_type: log.eventType ?? "Unknown",
      severity: log.severity ?? "warning",
      occurred_at: log.occurredAt,
      metadata: log.metadata ?? null,
    })),
  };
}

/**
 * One page of admin exam sessions. The server owns the scoping (a TeamLeader only ever gets
 * their own students' sessions) AND the filtering/paging, so the caller must not filter or slice
 * the result locally — doing so would contradict `totalCount` and blank out parts of a page.
 */
export async function fetchAllExamSessions(
  filter?: ExamSessionFilterParams,
): Promise<PaginatedResponse<any>> {
  if (!API_BASE_URL) {
    return { ...EMPTY_PAGE };
  }
  const query = new URLSearchParams();
  if (filter) {
    if (filter.search) query.append("search", filter.search);
    if (filter.status && filter.status !== "all") query.append("status", filter.status);
    if (filter.hasFlags === true) query.append("hasFlags", "true");
    if (filter.hasFlags === false) query.append("hasFlags", "false");
    if (filter.cameraIssues === true) query.append("cameraIssues", "true");
    if (filter.cameraIssues === false) query.append("cameraIssues", "false");
    if (filter.groupId) query.append("groupId", filter.groupId);
    if (filter.courseId) query.append("courseId", filter.courseId);
    if (filter.fromDate) query.append("fromDate", filter.fromDate);
    if (filter.toDate) query.append("toDate", filter.toDate);
    if (filter.pageNumber) query.append("pageNumber", String(filter.pageNumber));
    if (filter.pageSize) query.append("pageSize", String(filter.pageSize));
  }
  const qs = query.toString();
  const url = `${API_BASE_URL}/admin/exam-sessions${qs ? `?${qs}` : ""}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error("Failed to fetch all exam sessions");
  const raw = await res.json();
  // A bare array is the pre-pagination response shape — tolerated so a frontend deployed ahead
  // of the API still renders (as a single page) instead of showing an empty table.
  if (Array.isArray(raw)) {
    const items = raw.map(mapAdminExamSession);
    return {
      ...EMPTY_PAGE,
      items,
      totalCount: items.length,
      pageSize: items.length || EMPTY_PAGE.pageSize,
      totalPages: items.length > 0 ? 1 : 0,
    };
  }
  return {
    items: (Array.isArray(raw?.items) ? raw.items : []).map(mapAdminExamSession),
    totalCount: raw?.totalCount ?? 0,
    pageNumber: raw?.pageNumber ?? 1,
    pageSize: raw?.pageSize ?? 20,
    totalPages: raw?.totalPages ?? 0,
    hasNextPage: raw?.hasNextPage ?? false,
    hasPreviousPage: raw?.hasPreviousPage ?? false,
  };
}

export async function fetchSessionResponses(sessionId: string): Promise<GradingResponse[]> {
  if (!API_BASE_URL) {
    return [];
  }
  const res = await fetch(`${API_BASE_URL}/admin/exam-sessions/${sessionId}/responses`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    // Carry the server's reason through. The grading scope check returns a precise 403
    // ("You are not assigned to grade this course's exams."), and swallowing it left the
    // grading dialog unable to say anything more useful than "no responses".
    const reason = await res
      .json()
      .then((b) => b?.error ?? b?.message ?? null)
      .catch(() => null);
    throw new Error(reason || `Failed to fetch session responses (${res.status})`);
  }
  return await res.json();
}

export async function fetchQuestionBanks(): Promise<any[]> {
  if (!API_BASE_URL) {
    return [];
  }
  const res = await fetch(`${API_BASE_URL}/QuestionBanks`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error("Failed to fetch question banks");
  const raw = await res.json();
  // The UI reads the count as `bank.questions?.[0]?.count` (legacy Supabase
  // aggregate shape); expose it alongside the flat count.
  return (Array.isArray(raw) ? raw : []).map((b: any) => ({
    ...b,
    question_count: b.questionCount ?? 0,
    questions: [{ count: b.questionCount ?? 0 }],
    // Authorship drives the read-only affordances (see canManageBank in lib/rbac). A
    // ContentCreator lists every bank now, so the UI needs this to tell "mine" from
    // "someone else's" and disable Edit/Delete/Import accordingly.
    created_by: b.createdByAdminId ?? b.created_by ?? null,
  }));
}

export async function fetchQuestionBanksPaged(
  params: { pageNumber?: number; pageSize?: number; search?: string } = {},
): Promise<PaginatedResponse<any>> {
  if (!API_BASE_URL) return { ...EMPTY_PAGE };
  const qs = new URLSearchParams();
  if (params.pageNumber) qs.set("pageNumber", String(params.pageNumber));
  if (params.pageSize) qs.set("pageSize", String(params.pageSize));
  if (params.search) qs.set("search", params.search);
  const query = qs.toString() ? `?${qs.toString()}` : "";
  const res = await fetch(`${API_BASE_URL}/QuestionBanks/paged${query}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return { ...EMPTY_PAGE };
  const raw = await res.json();
  return {
    items: (Array.isArray(raw?.items) ? raw.items : []).map((b: any) => ({
      ...b,
      question_count: b.questionCount ?? 0,
      questions: [{ count: b.questionCount ?? 0 }],
      created_by: b.createdByAdminId ?? b.created_by ?? null,
    })),
    totalCount: raw?.totalCount ?? 0,
    pageNumber: raw?.pageNumber ?? 1,
    pageSize: raw?.pageSize ?? 20,
    totalPages: raw?.totalPages ?? 0,
    hasNextPage: raw?.hasNextPage ?? false,
    hasPreviousPage: raw?.hasPreviousPage ?? false,
  };
}

export async function fetchItemAnalysis(examId?: string): Promise<any> {
  // Item analysis is per-exam. Guard against being used as a bare queryFn (which
  // passes a QueryFunctionContext, not an id) or being called with no exam.
  if (!API_BASE_URL || typeof examId !== "string" || !examId) {
    return null;
  }
  const res = await fetch(`${API_BASE_URL}/admin/exam-analysis/${examId}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error("Failed to fetch item analysis");
  const data = await res.json();
  // ExamAnalysisDto is a single object (per-exam aggregate + a nested question list), not a flat
  // array — adapt to the snake_case shape the UI reads and rename fields to what the DTO actually
  // exposes (there's no per-question "type"/"attempts"/"avg time taken"; the closest real fields
  // are difficulty/times-answered/avg-response-seconds).
  const missed = data.mostMissedQuestions ?? data.MostMissedQuestions ?? [];
  return {
    exam_id: data.examId ?? data.ExamId,
    exam_title: data.examTitle ?? data.ExamTitle ?? "",
    attempt_count: data.attemptCount ?? data.AttemptCount ?? 0,
    completed_count: data.completedCount ?? data.CompletedCount ?? 0,
    average_score: data.averageScore ?? data.AverageScore ?? 0,
    pass_rate: data.passRate ?? data.PassRate ?? 0,
    fail_rate: data.failRate ?? data.FailRate ?? 0,
    average_completion_seconds: data.averageCompletionSeconds ?? data.AverageCompletionSeconds ?? 0,
    questions: (Array.isArray(missed) ? missed : []).map((q: any) => ({
      question_id: q.questionId ?? q.QuestionId,
      question_text: q.questionText ?? q.QuestionText ?? "",
      category: q.category ?? q.Category ?? null,
      difficulty: q.difficulty ?? q.Difficulty ?? null,
      times_asked: q.timesAsked ?? q.TimesAsked ?? 0,
      times_answered: q.timesAnswered ?? q.TimesAnswered ?? 0,
      percent_correct: q.percentCorrect ?? q.PercentCorrect ?? 0,
      avg_response_seconds: q.avgResponseSeconds ?? q.AvgResponseSeconds ?? 0,
    })),
  };
}

export async function fetchGradingQueue(examId?: string): Promise<GradingQueueItem[]> {
  if (!API_BASE_URL) {
    return [];
  }
  const qs = examId ? `?examId=${examId}` : "";
  const res = await fetch(`${API_BASE_URL}/admin/grading-queue${qs}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error("Failed to fetch grading queue");
  return await res.json();
}

export async function fetchGradingHistory(examId?: string): Promise<GradedHistoryItem[]> {
  if (!API_BASE_URL) {
    return [];
  }
  const qs = examId ? `?examId=${examId}` : "";
  const res = await fetch(`${API_BASE_URL}/admin/grading-history${qs}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error("Failed to fetch grading history");
  return await res.json();
}

export async function fetchStudentExamSessions(studentId: string): Promise<any[]> {
  if (!API_BASE_URL) {
    return [];
  }
  const res = await fetch(`${API_BASE_URL}/admin/students/${studentId}/exam-sessions`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) throw new Error("Failed to fetch student exam sessions");
  return await res.json();
}

/**
 * A freshly presigned URL for one session's biometric entry photo, or null when the session has
 * no photo (404). The link is valid for ~15 minutes from this call, so fetch it when the photo is
 * about to be shown rather than caching it alongside the session list.
 */
export async function fetchSessionEntryPhotoUrl(sessionId: string): Promise<string | null> {
  if (!API_BASE_URL) {
    return null;
  }
  const res = await fetch(`${API_BASE_URL}/admin/exam-sessions/${sessionId}/entry-photo`, {
    headers: getAuthHeaders(),
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    const reason = await res
      .json()
      .then((b) => b?.error ?? b?.message ?? null)
      .catch(() => null);
    throw new Error(reason ?? "Failed to load the entry photo.");
  }
  const data = await res.json();
  return data.url ?? null;
}

export type BiometricUploadResult =
  { ok: true; url: string } | { ok: false; status: number | null; message: string };

export async function uploadBiometricSelfie(selfieBlob: Blob): Promise<BiometricUploadResult> {
  if (!API_BASE_URL) {
    return { ok: true, url: URL.createObjectURL(selfieBlob) };
  }
  const formData = new FormData();
  // Explicitly set the filename so the backend FileService saves the file with a .jpg
  // extension (a raw Blob defaults to "blob" with no extension, and ASP.NET's static
  // file middleware refuses to serve extensionless files → 404 in the admin view).
  formData.append("file", selfieBlob, "selfie.jpg");
  // getAuthHeaders() hard-codes Content-Type: application/json. For a multipart
  // FormData body that header must be removed so the browser sets
  // "multipart/form-data; boundary=..." itself — otherwise the backend endpoint
  // ([Consumes("multipart/form-data")] + IFormFile) rejects the request with 415.
  const headers = getAuthHeaders();
  delete headers["Content-Type"];
  try {
    const res = await fetch(`${API_BASE_URL}/exams/biometrics/upload`, {
      method: "POST",
      headers,
      body: formData,
    });
    if (res.ok) {
      const data = await res.json();
      return { ok: true, url: data.url };
    }
    if (res.status === 401 || res.status === 403) {
      return { ok: false, status: res.status, message: "Session expired. Please log in again." };
    }
    return {
      ok: false,
      status: res.status,
      message: `Upload failed (${res.status}). Please retake your photo and try again.`,
    };
  } catch (err) {
    console.error("Biometric upload error", err);
    return { ok: false, status: null, message: "Network error. Check your connection and retry." };
  }
}
async function jsonOrThrow(res: Response) {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    // Carry the status alongside the message: callers that only read `.message` are unaffected,
    // but an ownership 403 is otherwise indistinguishable from any other failure, which is how a
    // "this exam was claimed by someone else" case ended up rendering as a generic save error.
    const error = new Error(err.error || res.statusText) as Error & { status?: number };
    error.status = res.status;
    throw error;
  }
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

/** Query params for the paged GET /Exams endpoint. Filtering happens entirely server-side. */
export interface ExamListParams {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  /** ExamType names, e.g. ["CourseFinal", "Assignment"]. OR within the facet. */
  categories?: string[];
  /** false → only exams not anchored to a checkpoint/course-final/track-final (unassigned). */
  isAssigned?: boolean;
  includeInactive?: boolean;
  isActive?: boolean;
  isVisible?: boolean;
  courseId?: string;
  trackId?: string;
  minDurationMinutes?: number;
  maxDurationMinutes?: number;
  minPassingScore?: number;
  maxPassingScore?: number;
  /** Exam.CreatedByAdminId — the staff user who authored the exam. */
  authorId?: string;
  /** Group-scoped exam filter — exams anchored to courses inside this group. */
  groupId?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
}

// Superset mapper: keep the original camelCase fields (other consumers read them) and add the
// snake_case aliases the exam-center builder/composer expects.
function mapExamRow(ex: any) {
  return {
    ...ex,
    passing_score: ex.passingScore ?? 0,
    time_limit_minutes: ex.timeLimitMinutes ?? 0,
    max_violations: ex.maxViolations ?? 0,
    is_active: ex.isActive ?? false,
    exam_type: ex.examType ?? "PracticeQuiz",
    randomize_questions: ex.shuffleQuestions ?? false,
    randomize_options: ex.shuffleAnswers ?? false,
    review_policy: ex.reviewPolicy ?? "Never",
    start_date: ex.startDate ?? null,
    end_date: ex.endDate ?? null,
    is_visible: ex.isVisible ?? true,
    is_biometric_required: ex.isBiometricRequired ?? false,
    course_id: ex.courseId ?? null,
    module_id: ex.moduleId ?? null,
    total_questions: ex.totalQuestions ?? 0,
    // Authorship drives the read-only affordances (see canEditExam in lib/rbac). The raw
    // camelCase field already arrives via the spread above; aliasing it keeps consumers from
    // having to know which casing this particular endpoint used.
    created_by: ex.createdByAdminId ?? ex.created_by ?? null,
  };
}

const EMPTY_PAGE: PaginatedResponse<any> = {
  items: [],
  totalCount: 0,
  pageNumber: 1,
  pageSize: 20,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

/**
 * Server-side paged/searched/filtered exam listing. The backend is the single source of truth for
 * category and structural-assignment enforcement — the caller only asks; it never filters locally.
 */
export async function fetchExamsPaged(
  params: ExamListParams = {},
): Promise<PaginatedResponse<any>> {
  if (!API_BASE_URL) return { ...EMPTY_PAGE };
  const qs = new URLSearchParams();
  if (params.pageNumber) qs.set("pageNumber", String(params.pageNumber));
  if (params.pageSize) qs.set("pageSize", String(params.pageSize));
  if (params.search) qs.set("search", params.search);
  (params.categories ?? []).forEach((c) => qs.append("categories", c));
  if (params.isAssigned !== undefined) qs.set("isAssigned", String(params.isAssigned));
  if (params.includeInactive) qs.set("includeInactive", "true");
  if (params.isActive !== undefined) qs.set("isActive", String(params.isActive));
  if (params.isVisible !== undefined) qs.set("isVisible", String(params.isVisible));
  if (params.courseId) qs.set("courseId", params.courseId);
  if (params.trackId) qs.set("trackId", params.trackId);
  if (params.minDurationMinutes !== undefined)
    qs.set("minDurationMinutes", String(params.minDurationMinutes));
  if (params.maxDurationMinutes !== undefined)
    qs.set("maxDurationMinutes", String(params.maxDurationMinutes));
  if (params.minPassingScore !== undefined)
    qs.set("minPassingScore", String(params.minPassingScore));
  if (params.maxPassingScore !== undefined)
    qs.set("maxPassingScore", String(params.maxPassingScore));
  if (params.authorId) qs.set("authorId", params.authorId);
  if (params.groupId)  qs.set("groupId",  params.groupId);
  if (params.sortBy) qs.set("sortBy", params.sortBy);
  if (params.sortDir) qs.set("sortDir", params.sortDir);

  const query = qs.toString() ? `?${qs.toString()}` : "";
  const res = await fetch(`${API_BASE_URL}/Exams${query}`, { headers: getAuthHeaders() });
  if (!res.ok) return { ...EMPTY_PAGE };
  const raw = await res.json();
  return {
    items: (Array.isArray(raw?.items) ? raw.items : []).map(mapExamRow),
    totalCount: raw?.totalCount ?? 0,
    pageNumber: raw?.pageNumber ?? 1,
    pageSize: raw?.pageSize ?? 20,
    totalPages: raw?.totalPages ?? 0,
    hasNextPage: raw?.hasNextPage ?? false,
    hasPreviousPage: raw?.hasPreviousPage ?? false,
  };
}

export interface HierarchyCourse {
  id: string;
  name: string;
  description?: string | null;
  sequence_order: number;
  is_owned: boolean;
  exams: any[];
}

export interface HierarchyTrack {
  id: string;
  name: string;
  description?: string | null;
  final_exam_id?: string | null;
  is_owned: boolean;
  courses: HierarchyCourse[];
}

export interface InstructorHierarchy {
  tracks: HierarchyTrack[];
  standalone_courses: HierarchyCourse[];
}

const EMPTY_HIERARCHY: InstructorHierarchy = { tracks: [], standalone_courses: [] };

/**
 * The whole Track → Course → Exam tree in ONE request.
 *
 * Deliberately not a per-course fetch: an instructor with a dozen courses would otherwise fire a
 * dozen `?courseId=` requests on load or on every expand. Everything arrives pre-ordered (courses
 * by track sequence, exams in teaching order) and unpaged, so expanding a node is a client-side
 * reveal — do NOT re-sort or paginate the exams here, the server order is the authoritative one.
 */
export async function fetchInstructorHierarchy(): Promise<InstructorHierarchy> {
  if (!API_BASE_URL) return { ...EMPTY_HIERARCHY };
  const res = await fetch(`${API_BASE_URL}/Exams/instructor-hierarchy`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return { ...EMPTY_HIERARCHY };
  const raw = await res.json();

  const mapCourse = (c: any): HierarchyCourse => ({
    id: c.id,
    name: c.name,
    description: c.description ?? null,
    sequence_order: c.sequenceOrder ?? 0,
    is_owned: c.isOwned ?? false,
    exams: (Array.isArray(c.exams) ? c.exams : []).map(mapExamRow),
  });

  return {
    tracks: (Array.isArray(raw?.tracks) ? raw.tracks : []).map((t: any) => ({
      id: t.id,
      name: t.name,
      description: t.description ?? null,
      final_exam_id: t.finalExamId ?? null,
      is_owned: t.isOwned ?? false,
      courses: (Array.isArray(t.courses) ? t.courses : []).map(mapCourse),
    })),
    standalone_courses: (Array.isArray(raw?.standaloneCourses) ? raw.standaloneCourses : []).map(
      mapCourse,
    ),
  };
}

/**
 * Convenience wrapper returning a flat exam array — for the exam-composer bank/type dropdowns that
 * want the whole (small) set. `categories` accepts one or more ExamType names. Walks all pages
 * sequentially. Prefer {@link fetchExamsPaged} for anything user-facing that should paginate/search.
 */
export async function fetchExams(
  includeInactive: boolean = false,
  categories?: string | string[],
): Promise<any[]> {
  const cats = categories ? (Array.isArray(categories) ? categories : [categories]) : undefined;
  // The server hard-caps pageSize at 100; asking for more silently truncates rather than erroring.
  // The iteration ceiling is a backstop against a totalCount the server never delivers.
  const allItems: any[] = [];
  for (let pageNumber = 1; pageNumber <= 25; pageNumber++) {
    const page = await fetchExamsPaged({
      includeInactive,
      categories: cats,
      pageSize: 100,
      pageNumber,
    });
    allItems.push(...page.items);
    if (page.items.length === 0 || allItems.length >= page.totalCount) break;
  }
  return allItems;
}

/**
 * Final-exam pickers — replaces the raw fetchExamsPaged call with three fixes:
 *   1. Always sends `includeInactive: true` (drafts are legal finals).
 *   2. Throws on non-2xx instead of returning an empty page, so the UI can
 *      distinguish "none exist" from "request failed."
 *   3. Returns each row's `is_active` so the caller can label drafts.
 */
export async function fetchFinalExamOptions(params: {
  kind: "CourseFinal" | "TrackFinal";
  search?: string;
  currentId?: string | null;
}): Promise<{ items: any[]; error?: string }> {
  const qs = new URLSearchParams();
  qs.set("categories", params.kind);
  qs.set("includeInactive", "true");
  qs.set("pageSize", "50");
  if (params.search) qs.set("search", params.search);

  const res = await fetch(`${API_BASE_URL}/Exams?${qs}`, { headers: getAuthHeaders() });
  if (!res.ok) {
    throw new Error(`Failed to load ${params.kind} exams (${res.status})`);
  }
  const raw = await res.json();
  const items = (Array.isArray(raw?.items) ? raw.items : []).map(mapExamRow);
  return { items };
}

/**
 * Create a question bank.
 *
 * `courseId` scopes the bank to a course. It is OPTIONAL for a SystemAdmin/ContentCreator (who may
 * create global banks) but REQUIRED for a caller holding only TeamLeader — the API answers 403
 * without it. Omitting it unconditionally, as this used to, made bank creation impossible for a
 * TeamLeader-only account.
 */
export async function createQuestionBank(
  name: string,
  description?: string,
  courseId?: string | null,
): Promise<any> {
  const res = await fetch(`${API_BASE_URL}/QuestionBanks`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({ name, description, courseId: courseId ?? null }),
  });
  return jsonOrThrow(res);
}

const UI_TO_BACKEND_QTYPE: Record<string, string> = {
  mcq: "MultipleChoice",
  multi_select: "MultiSelect",
  essay: "Essay",
  true_false: "TrueFalse",
  short_answer: "ShortAnswer",
};
const BACKEND_TO_UI_QTYPE: Record<string, string> = {
  MultipleChoice: "mcq",
  MultiSelect: "multi_select",
  Essay: "essay",
  TrueFalse: "true_false",
  ShortAnswer: "short_answer",
};

// Adapt the backend ExamQuestionAdminDto (typed options with isCorrect flags) to
// the legacy UI shape (option text[] + a correct letter / correct indices).
function mapBackendQuestionToUI(q: any): any {
  const options = [...(q.options ?? [])].sort(
    (a: any, b: any) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0),
  );
  const correctIndices = options
    .map((o: any, i: number) => (o.isCorrect ? i : -1))
    .filter((i: number) => i >= 0);
  const uiType = BACKEND_TO_UI_QTYPE[q.questionType] ?? "mcq";
  // TrueFalse behaves like a single-correct choice; ShortAnswer's options are acceptable answers.
  const singleCorrect = uiType === "mcq" || uiType === "true_false";
  return {
    id: q.id,
    bank_id: q.questionBankId,
    question_type: uiType,
    question_text: q.questionText ?? "",
    points: q.points ?? 1,
    difficulty: (q.difficulty ?? "Medium").toString().toLowerCase(),
    options: options.map((o: any) => o.text ?? ""),
    correct_answer:
      singleCorrect && correctIndices.length > 0
        ? String.fromCharCode(65 + correctIndices[0])
        : null,
    correct_options: uiType === "multi_select" || uiType === "short_answer" ? correctIndices : [],
    category: q.category ?? null,
    tags: q.tags ?? [],
    time_limit_seconds: q.timeLimitSeconds ?? 60,
  };
}

// Adapt the legacy UI question payload to the backend CreateExamQuestionDto.
function mapUIQuestionToBackend(payload: any): any {
  const uiType = payload.question_type ?? "mcq";
  const texts: string[] = uiType === "essay" ? [] : (payload.options ?? []);
  const singleCorrect = uiType === "mcq" || uiType === "true_false";
  const mcqIdx =
    typeof payload.correct_answer === "string" && payload.correct_answer
      ? payload.correct_answer.charCodeAt(0) - 65
      : -1;
  const multiIdx: number[] = payload.correct_options ?? [];
  const difficulty = payload.difficulty
    ? String(payload.difficulty).charAt(0).toUpperCase() +
      String(payload.difficulty).slice(1).toLowerCase()
    : "Medium";
  return {
    questionType: UI_TO_BACKEND_QTYPE[uiType] ?? "MultipleChoice",
    questionText: payload.question_text ?? "",
    timeLimitSeconds: payload.time_limit_seconds ?? 60,
    category: payload.category ?? null,
    points: Number(payload.points ?? 1),
    difficulty,
    orderIndex: payload.order_index ?? 0,
    // ShortAnswer: every supplied option is an acceptable answer (backend forces IsCorrect).
    options: texts.map((text: string, i: number) => ({
      text,
      isCorrect:
        uiType === "short_answer" ? true : singleCorrect ? i === mcqIdx : multiIdx.includes(i),
      orderIndex: i,
    })),
    tags: payload.tags ?? [],
  };
}

/** Query params for the paged GET /QuestionBanks/{bankId}/questions endpoint. */
export interface BankQuestionListParams {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
}

// Questions belong to a QuestionBank now — the argument is a bank id. A bank may hold tens of
// thousands of questions (including answer keys), so this is server-side paged/searched and never
// fetches the whole bank.
export async function fetchExamQuestionsByBank(
  bankId: string,
  params: BankQuestionListParams = {},
): Promise<PaginatedResponse<any>> {
  if (!API_BASE_URL || !bankId) return { ...EMPTY_PAGE };
  const qs = new URLSearchParams();
  if (params.pageNumber) qs.set("pageNumber", String(params.pageNumber));
  if (params.pageSize) qs.set("pageSize", String(params.pageSize));
  if (params.search) qs.set("search", params.search);
  const query = qs.toString() ? `?${qs.toString()}` : "";
  const res = await fetch(`${API_BASE_URL}/QuestionBanks/${bankId}/questions${query}`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return { ...EMPTY_PAGE };
  const raw = await res.json();
  return {
    items: (Array.isArray(raw?.items) ? raw.items : []).map(mapBackendQuestionToUI),
    totalCount: raw?.totalCount ?? 0,
    pageNumber: raw?.pageNumber ?? 1,
    pageSize: raw?.pageSize ?? 20,
    totalPages: raw?.totalPages ?? 0,
    hasNextPage: raw?.hasNextPage ?? false,
    hasPreviousPage: raw?.hasPreviousPage ?? false,
  };
}

/**
 * Soft-deletes an exam (DELETE /api/Exams/{examId}, role: ExamAuthoring).
 *
 * The server refuses with 400 + `{ error }` when the exam is still load-bearing — a checkpoint
 * gates on it, or it is wired as a course/track final. jsonOrThrow surfaces that exact message,
 * so the caller can show the real reason instead of a generic failure.
 */
export async function deleteExam(examId: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/Exams/${examId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  await jsonOrThrow(res);
}

export async function deleteExamQuestion(id: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/QuestionBanks/questions/${id}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  await jsonOrThrow(res);
}

/** Bank-level dependency warning, shown before the "delete all questions" confirmation. */
export interface BankQuestionsUsage {
  bank_id: string;
  question_count: number;
  in_use: boolean;
  active_exam_count: number;
  in_progress_session_count: number;
  message: string | null;
}

/**
 * Usage counts for a whole bank (GET /QuestionBanks/{bankId}/questions/usage).
 *
 * Advisory only — the clear endpoint never refuses. Returns null when the counts can't be read, so
 * the confirmation dialog degrades to its generic warning instead of blocking on a failed probe.
 */
export async function fetchBankQuestionsUsage(bankId: string): Promise<BankQuestionsUsage | null> {
  if (!API_BASE_URL || !bankId) return null;
  const res = await fetch(`${API_BASE_URL}/QuestionBanks/${bankId}/questions/usage`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return null;
  const raw = await res.json();
  return {
    bank_id: raw?.bankId ?? bankId,
    question_count: raw?.questionCount ?? 0,
    in_use: raw?.inUse ?? false,
    active_exam_count: raw?.activeExamCount ?? 0,
    in_progress_session_count: raw?.inProgressSessionCount ?? 0,
    message: raw?.message ?? null,
  };
}

/**
 * Empties a bank — soft-deletes every question in it (DELETE /QuestionBanks/{bankId}/questions).
 *
 * The bank itself survives, so exam bank rules pointing at it stay wired up (and will draw from an
 * empty pool until it is refilled). Returns how many questions the server hid.
 */
export async function deleteAllBankQuestions(bankId: string): Promise<number> {
  const res = await fetch(`${API_BASE_URL}/QuestionBanks/${bankId}/questions`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  const raw = await jsonOrThrow(res);
  return raw?.deletedCount ?? 0;
}

export async function updateExamQuestion(id: string, payload: any): Promise<any> {
  const res = await fetch(`${API_BASE_URL}/QuestionBanks/questions/${id}`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(mapUIQuestionToBackend(payload)),
  });
  return jsonOrThrow(res);
}

export async function createExamQuestion(payload: any): Promise<any> {
  const bankId =
    payload.bank_id ?? payload.bankId ?? payload.questionBankId ?? payload.question_bank_id;
  const res = await fetch(`${API_BASE_URL}/QuestionBanks/${bankId}/questions`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(mapUIQuestionToBackend(payload)),
  });
  return jsonOrThrow(res);
}

export async function fetchExamBankRules(examId: string): Promise<any[]> {
  if (!API_BASE_URL || !examId) return [];
  const res = await fetch(`${API_BASE_URL}/Exams/${examId}/bank-rules`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return [];
  const raw = await res.json();
  // UI composer reads `bank_id` + `draw_count`.
  return (Array.isArray(raw) ? raw : []).map((r: any) => ({
    id: r.id,
    bank_id: r.questionBankId,
    bank_name: r.questionBankName,
    draw_count: r.questionCount,
    available_in_bank: r.availableInBank,
    rule_tags: (r.ruleTags ?? []).map((rt: any) => ({
      id: rt.id,
      question_tag_id: rt.questionTagId,
      tag_name: rt.tagName,
      match_mode: rt.matchMode ?? "Required",
    })),
    fallback_to_untagged: r.fallbackToUntagged ?? false,
  }));
}

// Map the legacy snake_case exam form to the backend CreateExamDto/UpdateExamDto.
function mapUIExamToBackend(p: any): any {
  // Empty strings from cleared <Select>/<input> controls must become null so the
  // backend's Guid?/DateTime? binding doesn't 400 on "".
  const nn = (v: any) => (v === "" || v === undefined ? null : v);
  return {
    title: p.title ?? "",
    description: p.description ?? null,
    examType: p.exam_type ?? "PracticeQuiz",
    passingScore: Number(p.passing_score ?? 70),
    timeLimitMinutes: Number(p.time_limit_minutes ?? 60),
    isActive: p.is_active ?? true,
    isBiometricRequired: p.is_biometric_required ?? false,
    maxViolations: Number(p.max_violations ?? 3),
    shuffleQuestions: p.randomize_questions ?? false,
    shuffleAnswers: p.randomize_options ?? false,
    startDate: nn(p.start_date),
    endDate: nn(p.end_date),
    isVisible: p.is_visible ?? true,
    reviewPolicy: p.review_policy ?? "Never",
    instructions: p.instructions ?? null,
    courseId: nn(p.course_id),
    moduleId: nn(p.module_id),
    bankRules: (p.bank_rules ?? []).map((r: any) => ({
      questionBankId: r.bank_id,
      questionCount: Number(r.question_count ?? r.draw_count ?? 0),
      ruleTags: (r.rule_tags ?? []).map((rt: any) => ({
        questionTagId: rt.question_tag_id,
        matchMode: rt.match_mode ?? "Required",
      })),
      fallbackToUntagged: r.fallback_to_untagged ?? false,
    })),
  };
}

export async function createExam(payload: any): Promise<any> {
  const res = await fetch(`${API_BASE_URL}/Exams`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(mapUIExamToBackend(payload)),
  });
  return jsonOrThrow(res);
}

export async function updateExam(id: string, payload: any): Promise<any> {
  const res = await fetch(`${API_BASE_URL}/Exams/${id}`, {
    method: "PUT",
    headers: getAuthHeaders(),
    body: JSON.stringify(mapUIExamToBackend(payload)),
  });
  return jsonOrThrow(res);
}

// ── Exam target assignments (Track / Course / User) ──────────────────────────
export interface ExamAssignment {
  id: string;
  exam_id: string;
  track_id: string | null;
  track_name: string | null;
  course_id: string | null;
  course_name: string | null;
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
  extra_time_minutes: number;
  start_date: string | null;
  end_date: string | null;
}

function mapExamAssignment(a: any): ExamAssignment {
  return {
    id: a.id ?? a.Id,
    exam_id: a.examId ?? a.exam_id,
    track_id: a.trackId ?? a.track_id ?? null,
    track_name: a.trackName ?? a.track_name ?? null,
    course_id: a.courseId ?? a.course_id ?? null,
    course_name: a.courseName ?? a.course_name ?? null,
    user_id: a.userId ?? a.user_id ?? null,
    user_name: a.userName ?? a.user_name ?? null,
    user_email: a.userEmail ?? a.user_email ?? null,
    extra_time_minutes: a.extraTimeMinutes ?? a.extra_time_minutes ?? 0,
    start_date: a.startDate ?? a.start_date ?? null,
    end_date: a.endDate ?? a.end_date ?? null,
  };
}

export async function fetchExamAssignments(examId: string): Promise<ExamAssignment[]> {
  if (!API_BASE_URL || !examId) return [];
  const res = await fetch(`${API_BASE_URL}/Exams/${examId}/assignments`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return [];
  const raw = await res.json();
  return (Array.isArray(raw) ? raw : []).map(mapExamAssignment);
}

export async function createExamAssignment(
  examId: string,
  payload: {
    trackId?: string | null;
    courseId?: string | null;
    userId?: string | null;
    extraTimeMinutes?: number;
    startDate?: string | null;
    endDate?: string | null;
  },
): Promise<ExamAssignment> {
  const res = await fetch(`${API_BASE_URL}/Exams/${examId}/assignments`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify({
      trackId: payload.trackId ?? null,
      courseId: payload.courseId ?? null,
      userId: payload.userId ?? null,
      extraTimeMinutes: Number(payload.extraTimeMinutes ?? 0),
      startDate: payload.startDate ?? null,
      endDate: payload.endDate ?? null,
    }),
  });
  return mapExamAssignment(await jsonOrThrow(res));
}

export async function deleteExamAssignment(id: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/Exams/assignments/${id}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  if (!res.ok && res.status !== 204) await jsonOrThrow(res);
}

// Grade an essay response (final score is recalculated server-side).
// pointsEarned (points awarded out of the question's weight) is the primary input; the legacy
// score% is only sent when explicitly provided (non-UI callers).
export async function updateExamResponseGrade(
  responseId: string,
  payload: { pointsEarned?: number | null; score?: number | null; note?: string | null },
): Promise<void> {
  const body: Record<string, unknown> = { note: payload.note };
  if (payload.pointsEarned !== undefined && payload.pointsEarned !== null) {
    body.pointsEarned = payload.pointsEarned;
  } else if (payload.score !== undefined && payload.score !== null) {
    body.score = payload.score;
  }
  const res = await fetch(`${API_BASE_URL}/admin/grading-queue/${responseId}/grade`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(body),
  });
  await jsonOrThrow(res);
}

export async function updateExamSubmissionQueueStatus(
  _queueId: string,
  _status: string,
): Promise<void> {
  // Session status is derived server-side from grading; no separate endpoint needed.
  return;
}

export async function fetchStudentExamAssignmentsAndSessions(studentId: string): Promise<any[]> {
  if (!API_BASE_URL) return [];

  // 1. Fetch all active exams (when logged in as Admin, returns all active exams)
  const examsRes = await fetch(`${API_BASE_URL}/Exams`, { headers: getAuthHeaders() });
  if (!examsRes.ok) throw new Error("Failed to fetch exams");
  const exams = await examsRes.json();

  // 2. Fetch all exam sessions
  const sessionsRes = await fetch(`${API_BASE_URL}/admin/exam-sessions`, {
    headers: getAuthHeaders(),
  });
  if (!sessionsRes.ok) throw new Error("Failed to fetch exam sessions");
  const sessions = await sessionsRes.json();

  // Filter sessions for the specific student
  const studentSessions = sessions.filter(
    (s: any) => (s.studentId ?? s.student_id ?? s.userId ?? s.user_id) === studentId,
  );

  // 3. Map exams to include their sessions
  return exams.map((exam: any) => {
    const examSessions = studentSessions.filter((s: any) => (s.examId ?? s.exam_id) === exam.id);
    return {
      exam_id: exam.id,
      exam: {
        id: exam.id,
        title: exam.title || exam.name,
        passing_score: exam.passingScore || exam.passing_score || 70,
      },
      sessions: examSessions.map((s: any) => ({
        id: s.id,
        passed: s.passed,
        score: s.score,
        status: s.status?.toLowerCase(),
        completed_at: s.completedAt || s.completed_at,
      })),
    };
  });
}

export async function fetchPastExamSessionsBatch(
  userId: string,
  examIds: string[],
): Promise<Record<string, any>> {
  if (!API_BASE_URL || examIds.length === 0) return {};
  const res = await fetch(
    `${API_BASE_URL}/exams/sessions/past/batch?userId=${userId}&examIds=${examIds.join(",")}`,
    { headers: getAuthHeaders() },
  );
  return res.ok ? await res.json() : {};
}

// ── Tag & Pool Analysis API functions ──────────────────────────────────────

export interface QuestionTagDetail {
  id: string;
  name: string;
  question_count: number;
  used_by_rule_count: number;
}

export interface PoolAnalysis {
  bank_id: string;
  requested_count: number;
  eligible_count: number;
  untagged_count: number;
  total_in_bank: number;
  is_sufficient: boolean;
  deficit: number;
  tag_breakdown: { tag_id: string; tag_name: string; question_count: number }[];
  warnings: string[];
}

export async function fetchTagsForBank(bankId: string): Promise<QuestionTagDetail[]> {
  if (!API_BASE_URL || !bankId) return [];
  const res = await fetch(`${API_BASE_URL}/questionbanks/${bankId}/tags`, {
    headers: getAuthHeaders(),
  });
  if (!res.ok) return [];
  const raw = await res.json();
  return (Array.isArray(raw) ? raw : []).map((t: any) => ({
    id: t.id,
    name: t.name,
    question_count: t.questionCount,
    used_by_rule_count: t.usedByRuleCount,
  }));
}

export async function analyzePool(
  bankId: string,
  tags: { tagId: string; matchMode: string }[],
  requestedCount: number,
): Promise<PoolAnalysis> {
  const res = await fetch(`${API_BASE_URL}/questionbanks/${bankId}/analyze`, {
    method: "POST",
    headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ tags, requestedCount }),
  });
  if (!res.ok) throw new Error("Failed to analyze pool");
  const r = await res.json();
  return {
    bank_id: r.bankId,
    requested_count: r.requestedCount,
    eligible_count: r.eligibleCount,
    untagged_count: r.untaggedCount,
    total_in_bank: r.totalInBank,
    is_sufficient: r.isSufficient,
    deficit: r.deficit,
    tag_breakdown: (r.tagBreakdown ?? []).map((b: any) => ({
      tag_id: b.tagId,
      tag_name: b.tagName,
      question_count: b.questionCount,
    })),
    warnings: r.warnings ?? [],
  };
}

export async function previewPool(
  bankId: string,
  tags: { tagId: string; matchMode: string }[],
  requestedCount: number,
): Promise<any[]> {
  const res = await fetch(`${API_BASE_URL}/questionbanks/${bankId}/preview-pool`, {
    method: "POST",
    headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ tags, requestedCount }),
  });
  if (!res.ok) throw new Error("Failed to preview pool");
  return res.json();
}

export async function renameTag(tagId: string, name: string): Promise<any> {
  const res = await fetch(`${API_BASE_URL}/questionbanks/tags/${tagId}`, {
    method: "PUT",
    headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error("Failed to rename tag");
  return res.json();
}

/**
 * Delete (soft-delete) a question bank.
 *
 * The endpoint refuses a bank an exam still draws from and answers 400 with
 * `{ error: "Cannot delete a bank that is used by one or more exams." }` — that reason is
 * surfaced verbatim so the admin knows to detach the exam's bank rule first.
 */
export async function deleteQuestionBank(bankId: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/QuestionBanks/${bankId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    const raw = await res.text().catch(() => "");
    let message = raw.trim();
    try {
      const parsed = JSON.parse(message);
      message = parsed?.error ?? parsed?.message ?? parsed?.title ?? message;
    } catch {
      /* plain-text body */
    }
    if (res.status === 403) message ||= "You do not have permission to delete this bank.";
    if (res.status === 404) message ||= "Bank not found or already deleted.";
    throw new Error(message || `Failed to delete bank (${res.status})`);
  }
}

export async function deleteTag(tagId: string): Promise<void> {
  const res = await fetch(`${API_BASE_URL}/questionbanks/tags/${tagId}`, {
    method: "DELETE",
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error ?? "Failed to delete tag");
  }
}
